import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  FiAward,
  FiChevronLeft,
  FiChevronRight,
  FiDroplet,
  FiGrid,
  FiShoppingBag,
  FiThermometer,
  FiTool,
} from "react-icons/fi";
import {
  MdOutlineBlender,
  MdOutlineCoffeeMaker,
  MdOutlineKitchen,
  MdOutlineMicrowave,
  MdOutlineRiceBowl,
} from "react-icons/md";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import Heading from "../../components/Heading";

const MainCategory = () => {
  const categories = useSelector((state) => state.maincategory.categories);
  const desiredOrder = [
    "Cooking",
    "Coffee",
    "Blenders",
    "Refrigeration",
    "Small Appliances",
  ];
  const [activeSlide, setActiveSlide] = useState(0);

  const sortCategories = (categories) => {
    return categories?.sort((a, b) => {
      return desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name);
    });
  };

  const filteredCategory = (categories?.data || categories)?.filter(
    (category) => !category?.parent?.id
  );

  const mainCategories = sortCategories([...filteredCategory]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const NextArrow = ({ onClick }) => (
    <motion.button
      className="absolute right-0 z-10 flex items-center justify-center w-12 h-12 -mr-6 -translate-y-1/2 bg-white rounded-md shadow-lg top-1/2"
      onClick={onClick}
      whileHover={{ scale: 1.05, backgroundColor: "#f8f8f8" }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0.7 }}
      animate={{ opacity: 1 }}
    >
      <FiChevronRight className="text-slate-700" size={24} />
    </motion.button>
  );

  const PrevArrow = ({ onClick }) => (
    <motion.button
      className="absolute left-0 z-10 flex items-center justify-center w-12 h-12 -ml-6 -translate-y-1/2 bg-white rounded-md shadow-lg top-1/2"
      onClick={onClick}
      whileHover={{ scale: 1.05, backgroundColor: "#f8f8f8" }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0.7 }}
      animate={{ opacity: 1 }}
    >
      <FiChevronLeft className="text-slate-700" size={24} />
    </motion.button>
  );

  const CustomDot = ({ onClick, active }) => (
    <motion.div
      onClick={onClick}
      className="inline-block mx-1 cursor-pointer"
      initial={{ opacity: 0.5 }}
      animate={{
        opacity: active ? 1 : 0.5,
        scale: active ? 1.2 : 1,
      }}
      whileHover={{ scale: 1.3 }}
    >
      <div className="relative">
        <div
          className={`w-6 h-2 rounded-lg ${
            active ? "bg-amber-500" : "bg-slate-300"
          }`}
        ></div>
        {active && (
          <motion.div
            className="absolute inset-0 rounded-lg bg-amber-500"
            initial={{ opacity: 0.3, scale: 1 }}
            animate={{
              opacity: 0,
              scale: 1.8,
              transition: { repeat: Number.POSITIVE_INFINITY, duration: 1.5 },
            }}
          />
        )}
      </div>
    </motion.div>
  );

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    beforeChange: (current, next) => setActiveSlide(next),
    customPaging: (i) => <CustomDot active={i === activeSlide} />,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: i * 0.1,
      },
    }),
    hover: {
      y: -8,
      boxShadow:
        "0 15px 30px -5px rgba(0, 0, 0, 0.1), 0 10px 15px -5px rgba(0, 0, 0, 0.05)",
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const imageVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.4 },
    },
  };

  const getCategoryIcon = (name) => {
    switch (name?.toLowerCase()) {
      case "cooking":
        return <MdOutlineKitchen size={18} />;
      case "coffee":
        return <MdOutlineCoffeeMaker size={18} />;
      case "blenders":
        return <MdOutlineBlender size={18} />;
      case "refrigeration":
        return <FiThermometer size={18} />;
      case "small appliances":
        return <MdOutlineMicrowave size={18} />;
      case "food processors":
        return <MdOutlineRiceBowl size={18} />;
      case "mixers":
        return <FiTool size={18} />;
      case "dishwashers":
        return <FiDroplet size={18} />;
      default:
        return <FiGrid size={18} />;
    }
  };

  return (
    <div className="relative py-16 overflow-hidden bg-slate-50">
      <div className="absolute top-0 left-0 w-full h-64 opacity-10 -z-10">
        <div className="absolute w-64 h-64 rounded-full bg-amber-300 -top-20 -left-20"></div>
        <div className="absolute w-48 h-48 rounded-full bg-slate-300 top-40 right-20"></div>
      </div>

      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <Heading title="Shop by Category" />
          </motion.div>
        </div>

        {mainCategories?.length > 4 ? (
          <div className="">
            <Slider {...sliderSettings} className="category-slider">
              {mainCategories.map((cat, index) => (
                <div key={index} className="px-2">
                  <CategoryCard
                    cat={cat}
                    index={index}
                    cardVariants={cardVariants}
                    imageVariants={imageVariants}
                    getCategoryIcon={getCategoryIcon}
                  />
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {mainCategories?.map((cat, index) => (
              <CategoryCard
                key={index}
                cat={cat}
                index={index}
                cardVariants={cardVariants}
                imageVariants={imageVariants}
                getCategoryIcon={getCategoryIcon}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const CategoryCard = ({
  cat,
  index,
  cardVariants,
  imageVariants,
  getCategoryIcon,
}) => {
  const fallbackImage =
    "https://images.unsplash.com/photo-1556911220-bff31c812dba?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=60";

  return (
    <Link to={`/${cat?.slug}`}>
      <motion.div
        className="overflow-hidden transition-all duration-300 bg-white border rounded-lg shadow-sm border-slate-100 hover:shadow-xl"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        custom={index}
      >
        <div className="relative overflow-hidden aspect-[4/3] group">
          <motion.img
            src={cat?.image?.url || fallbackImage}
            alt={cat?.name || "Category"}
            className="object-cover w-full h-full transition-transform"
            variants={imageVariants}
            onError={(e) => {
              e.target.src = fallbackImage;
            }}
          />
          <motion.div
            className="absolute inset-0 opacity-0 bg-black/30 group-hover:opacity-100"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />

          <motion.div
            className="absolute bottom-0 left-0 right-0 p-4 transition-transform duration-300 translate-y-full group-hover:translate-y-0"
            initial={{ y: 20, opacity: 0 }}
            whileHover={{ y: 0, opacity: 1 }}
          >
            <motion.div
              className="flex items-center justify-center px-3 py-2 space-x-1 text-xs font-medium text-white border rounded-md border-white/30 backdrop-blur-sm w-fit"
              whileHover={{ scale: 1.05 }}
            >
              <FiAward size={12} />
              <span>Premium Quality</span>
            </motion.div>
          </motion.div>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <motion.div
                className="flex items-center justify-center w-8 h-8 rounded-md text-amber-600 bg-amber-50"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {getCategoryIcon(cat?.name)}
              </motion.div>
              <h3 className="text-lg font-medium text-slate-800">
                {cat?.name}
              </h3>
            </div>

            <motion.div
              className="flex items-center justify-center w-8 h-8 text-white rounded-md bg-slate-800"
              whileHover={{ scale: 1.1, backgroundColor: "#f59e0b" }}
              whileTap={{ scale: 0.95 }}
            >
              <FiShoppingBag size={16} />
            </motion.div>
          </div>

          {cat?.description && (
            <p className="mt-2 text-sm text-slate-500 line-clamp-1">
              {cat.description}
            </p>
          )}

          <div className="flex items-center justify-between mt-4">
            <div className="flex space-x-1">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className={`w-1.5 h-6 ${
                    i < 3 ? "bg-amber-500" : "bg-slate-200"
                  } rounded-sm`}
                  initial={{ height: 12 }}
                  whileHover={{ height: 24 }}
                  transition={{ duration: 0.2, delay: i * 0.05 }}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default MainCategory;
